// Unified color priority engine for TradeReady Calendar events
// This is the single source of truth for ALL event colors across the calendar system

export type ConflictSeverity = "critical" | "high" | "medium" | "low";

/**
 * Conflict severity to color mapping (same as backend)
 * Maps severity levels to their visual representations
 */
export const conflictColorMap: Record<ConflictSeverity, string> = {
  critical: "#F97316",  // Orange-600 - overlapping events, safety critical
  high: "#F59E0B",      // Amber-500 - same time, different staff
  medium: "#EAB308",    // Yellow-500 - within 2 hours
  low: "#84CC16",       // Lime-500 - same day, >2 hours apart
};

/**
 * Provider color mapping for calendar source badges
 * Returns actual hex color values for visual consistency
 */
export const providerColorMap: Record<string, string> = {
  google: "#34A853",     // Google green
  outlook: "#0078D4",    // Microsoft blue
  apple: "#000000",      // Apple black
  workdeck: "#6B7280",   // Gray-500 (default)
};

interface EventColorInput {
  conflictColor?: string;          // Severity label from backend: "critical" | "high" | "medium" | "low"
  statusColor?: string;            // Job status color (e.g., "#3B82F6")
  jobTypeColor?: string;           // Job type color if set
  providerColor?: string;          // Provider-specific color
  source?: string;                 // Calendar source: "google" | "outlook" | "apple" | "workdeck"
}

/**
 * Resolve the final display color for a calendar event based on priority rules
 * 
 * Priority order (highest to lowest):
 * 1. Conflict color (safety critical) - from conflictColorMap
 * 2. Status color (business logic) - from job status
 * 3. Job type color (categorization) - from job type settings
 * 4. Provider color (calendar source) - from providerColorMap
 * 5. Default fallback - gray
 * 
 * This function is the ONLY place color priority is decided.
 * All calendar rendering MUST use this function's output.
 */
export function resolveEventColor(event: EventColorInput): string {
  // Priority 1: Conflict color (highest - safety critical)
  // Backend sends severity label ("critical", "high", etc.) - map to actual color
  if (event.conflictColor) {
    return conflictColorMap[event.conflictColor as ConflictSeverity] || event.conflictColor;
  }

  // Priority 2: Status color (business logic)
  if (event.statusColor) {
    return event.statusColor;
  }

  // Priority 3: Job type color (categorization)
  if (event.jobTypeColor) {
    return event.jobTypeColor;
  }

  // Priority 4: Provider color (calendar source)
  if (event.source && event.source !== 'workdeck') {
    return providerColorMap[event.source.toLowerCase()] || providerColorMap.workdeck;
  }

  // Priority 5: Default fallback
  return "#6B7280"; // Gray-500
}

/**
 * Resolve provider badge color for calendar source indicators
 * Returns the actual hex color for the provider's badge dot
 * 
 * This is separate from resolveEventColor because:
 * - Provider badges are always shown (when source !== 'workdeck')
 * - They use the provider color regardless of conflict/status priority
 * - This ensures visual consistency in multi-source calendars
 */
export function resolveProviderBadge(event: EventColorInput): string {
  if (!event.source || event.source === 'workdeck') {
    return providerColorMap.workdeck;
  }

  return providerColorMap[event.source.toLowerCase()] || providerColorMap.workdeck;
}

/**
 * Add alpha transparency to a hex color
 * @param color - Hex color (e.g., "#FF0000")
 * @param alpha - Alpha value from 0-1 (e.g., 0.12 for 12%)
 * @returns rgba() color string
 */
export function withAlpha(color: string, alpha: number): string {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // If already rgba, return as-is
  return color;
}
